'use client';

import { useRef } from 'react';
import gsap from 'gsap';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import CustomLink from 'components/shared/CustomLink';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { homeAssetSrc } from './homeAssetSrc';
import { bindOnceTextReveal } from './scrollBeat';
import { homepageCtaProps } from './homepageCta';
import v3 from './v3.module.css';
import styles from './BiomarkersScienceSection.module.css';

const PINNED_MEDIA_QUERY = '(prefers-reduced-motion: no-preference)';
const DESKTOP_MIN = 992;
const EDGE_INSET = 8;
const SAFE_GAP = 28;
const DESKTOP_SIZE_SCALE = 0.8;
const DESKTOP_FRAME_W = 1600;
const DESKTOP_FRAME_H = 900;
const MOBILE_FRAME_W = 390;
const MOBILE_FRAME_H = 844;
const MOBILE_Y_COMPRESS = 0.85;
const MOBILE_X_PULL = 0.88;
const MOBILE_SCRUB_LAG = 0.35;
const MOBILE_SCROLL_START = 'top 80%';
const DESKTOP_SCROLL_START = 'top bottom';
const COARSE_POINTER_QUERY = '(pointer: coarse)';
const DESKTOP_LAYOUT_QUERY = `(min-width: ${DESKTOP_MIN}px)`;
const ORBIT_EASE = gsap.parseEase('power2.inOut');

const mobileMaxCenterY = (stageH: number, cardH: number, orbit = 1): number =>
  stageH - (cardH * orbit) / 2;

type Box = { x: number; y: number; w: number; h: number };

type CollageKey =
  | 'blockBackLeft'
  | 'blockFrontLeft'
  | 'flowerImageCard'
  | 'blockBackRight'
  | 'blockFrontRight'
  | 'wavesImageCard';

const STATE_B: Record<CollageKey, Box> = {
  blockBackLeft: {
    x: 8 / DESKTOP_FRAME_W,
    y: 92 / DESKTOP_FRAME_H,
    w: 256 / DESKTOP_FRAME_W,
    h: 280 / DESKTOP_FRAME_H,
  },
  blockFrontLeft: {
    x: 300 / DESKTOP_FRAME_W,
    y: 158 / DESKTOP_FRAME_H,
    w: 359 / DESKTOP_FRAME_W,
    h: 185 / DESKTOP_FRAME_H,
  },
  flowerImageCard: {
    x: 1080 / DESKTOP_FRAME_W,
    y: 0 / DESKTOP_FRAME_H,
    w: 320 / DESKTOP_FRAME_W,
    h: 320 / DESKTOP_FRAME_H,
  },
  blockBackRight: {
    x: 1280 / DESKTOP_FRAME_W,
    y: 610 / DESKTOP_FRAME_H,
    w: 256 / DESKTOP_FRAME_W,
    h: 246 / DESKTOP_FRAME_H,
  },
  blockFrontRight: {
    x: 1220 / DESKTOP_FRAME_W,
    y: 513 / DESKTOP_FRAME_H,
    w: 359 / DESKTOP_FRAME_W,
    h: 185 / DESKTOP_FRAME_H,
  },
  wavesImageCard: {
    x: 40 / DESKTOP_FRAME_W,
    y: 608 / DESKTOP_FRAME_H,
    w: 376 / DESKTOP_FRAME_W,
    h: 240 / DESKTOP_FRAME_H,
  },
};

const STATE_MOBILE: Record<CollageKey, Box> = {
  blockBackLeft: {
    x: 11 / MOBILE_FRAME_W,
    y: 135.28 / MOBILE_FRAME_H,
    w: 113.5 / MOBILE_FRAME_W,
    h: 124.14 / MOBILE_FRAME_H,
  },
  blockFrontLeft: {
    x: -16.34 / MOBILE_FRAME_W,
    y: 84.54 / MOBILE_FRAME_H,
    w: 159.16 / MOBILE_FRAME_W,
    h: 82.02 / MOBILE_FRAME_H,
  },
  flowerImageCard: {
    x: 257.59 / MOBILE_FRAME_W,
    y: 58 / MOBILE_FRAME_H,
    w: 141.87 / MOBILE_FRAME_W,
    h: 141.87 / MOBILE_FRAME_H,
  },
  blockFrontRight: {
    x: 292.44 / MOBILE_FRAME_W,
    y: 633.93 / MOBILE_FRAME_H,
    w: 159.16 / MOBILE_FRAME_W,
    h: 82.02 / MOBILE_FRAME_H,
  },
  blockBackRight: {
    x: 245.4 / MOBILE_FRAME_W,
    y: 676.94 / MOBILE_FRAME_H,
    w: 113.5 / MOBILE_FRAME_W,
    h: 109.06 / MOBILE_FRAME_H,
  },
  wavesImageCard: {
    x: -33.53 / MOBILE_FRAME_W,
    y: 596.05 / MOBILE_FRAME_H,
    w: 166.7 / MOBILE_FRAME_W,
    h: 106.4 / MOBILE_FRAME_H,
  },
};

const SCIENCE_IMAGES: Record<CollageKey, string> = {
  blockBackLeft: homeAssetSrc('web_v2/home-v4/science-rev/01.png'),
  blockFrontLeft: homeAssetSrc('web_v2/home-v4/science-rev/02.png'),
  flowerImageCard: homeAssetSrc('web_v2/home-v4/science-rev/03.png'),
  blockFrontRight: homeAssetSrc('web_v2/home-v4/science-rev/04.png'),
  blockBackRight: homeAssetSrc('web_v2/home-v4/science-rev/05.png'),
  wavesImageCard: homeAssetSrc('web_v2/home-v4/science-rev/06.png'),
};

const COLLAGE_Z: Record<CollageKey, number> = {
  blockBackLeft: 1,
  blockBackRight: 1,
  wavesImageCard: 2,
  flowerImageCard: 2,
  blockFrontLeft: 3,
  blockFrontRight: 3,
};

const COLLAGE_ITEMS: Array<{
  key: CollageKey;
  travel: number;
  dir: 1 | -1;
  appearAt: number;
  duration: number;
}> = [
  { key: 'blockBackLeft', travel: 0.85, dir: 1, appearAt: 0, duration: 0.62 },
  { key: 'blockFrontLeft', travel: 0.8, dir: 1, appearAt: 0.1, duration: 0.58 },
  { key: 'flowerImageCard', travel: 0.9, dir: 1, appearAt: 0.04, duration: 0.7 },
  { key: 'wavesImageCard', travel: 0.85, dir: 1, appearAt: 0.14, duration: 0.6 },
  {
    key: 'blockFrontRight',
    travel: 0.8,
    dir: 1,
    appearAt: 0.07,
    duration: 0.66,
  },
  { key: 'blockBackRight', travel: 0.9, dir: 1, appearAt: 0.2, duration: 0.55 },
];

const COLLAGE_ANIM_END = Math.max(
  ...COLLAGE_ITEMS.map((item) => item.appearAt + item.duration),
);

type OvalGeom = {
  stageW: number;
  stageH: number;
  headerPad: number;
  contentH: number;
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  isDesktop: boolean;
};

const boxAngle = (box: Box): number =>
  Math.atan2(box.y + box.h / 2 - 0.5, box.x + box.w / 2 - 0.5);

const desktopAngles = (box: Box): { start: number; end: number } => {
  const end = boxAngle(box);
  return { start: end - Math.PI * 0.7, end };
};

const measureGeom = (stage: HTMLElement): OvalGeom => {
  const rect = stage.getBoundingClientRect();
  const stageW = rect.width;
  const stageH = rect.height;
  const headerPad = parseFloat(getComputedStyle(stage).paddingTop) || 0;
  const contentH = Math.max(stageH - headerPad, 1);
  const cx = stageW / 2;
  const cy = headerPad + contentH / 2;
  const isDesktop = stageW >= DESKTOP_MIN;

  const textHalfW = Math.min(stageW * 0.38, 360);
  const textHalfH = Math.min(contentH * 0.16, 100);

  const desktopScale = (stageW / DESKTOP_FRAME_W) * DESKTOP_SIZE_SCALE;
  const desktopCardHalfW =
    (Math.max(...COLLAGE_ITEMS.map(({ key }) => STATE_B[key].w)) *
      DESKTOP_FRAME_W *
      desktopScale) /
    2;
  const desktopCardHalfH =
    (Math.max(...COLLAGE_ITEMS.map(({ key }) => STATE_B[key].h)) *
      DESKTOP_FRAME_H *
      desktopScale) /
    2;

  if (!isDesktop) {
    return {
      stageW,
      stageH,
      headerPad,
      contentH,
      cx,
      cy,
      rx: stageW * 0.48,
      ry: contentH * 0.34,
      isDesktop,
    };
  }

  const maxRx = Math.max(48, stageW / 2 - desktopCardHalfW - EDGE_INSET);
  const maxRy = Math.max(48, contentH / 2 - desktopCardHalfH - EDGE_INSET);
  const clearRx = textHalfW + desktopCardHalfW + SAFE_GAP;
  const clearRy = textHalfH + desktopCardHalfH + SAFE_GAP;

  return {
    stageW,
    stageH,
    headerPad,
    contentH,
    cx,
    cy,
    rx: gsap.utils.clamp(Math.min(clearRx, maxRx), maxRx, stageW * 0.4),
    ry: gsap.utils.clamp(Math.min(clearRy, maxRy), maxRy, stageH * 0.36),
    isDesktop,
  };
};

const cardSize = (key: CollageKey, geom: OvalGeom): { w: number; h: number } => {
  if (geom.isDesktop) {
    const box = STATE_B[key];
    const scale = (geom.stageW / DESKTOP_FRAME_W) * DESKTOP_SIZE_SCALE;
    return {
      w: box.w * DESKTOP_FRAME_W * scale,
      h: box.h * DESKTOP_FRAME_H * scale,
    };
  }

  // Width-referenced scale so tall phones don't vertically inflate the collage.
  const box = STATE_MOBILE[key];
  const scale = geom.stageW / MOBILE_FRAME_W;
  return {
    w: box.w * MOBILE_FRAME_W * scale,
    h: box.h * MOBILE_FRAME_H * scale,
  };
};

const mobileFinalCenter = (
  key: CollageKey,
  geom: OvalGeom,
): { x: number; y: number } => {
  const box = STATE_MOBILE[key];
  const scale = geom.stageW / MOBILE_FRAME_W;
  const cardCx = (box.x + box.w / 2) * MOBILE_FRAME_W;
  const cardCy = (box.y + box.h / 2) * MOBILE_FRAME_H;
  const { w, h } = cardSize(key, geom);

  const x = geom.cx + (cardCx - MOBILE_FRAME_W / 2) * scale * MOBILE_X_PULL;
  const y = geom.cy + (cardCy - MOBILE_FRAME_H / 2) * scale * MOBILE_Y_COMPRESS;

  return {
    x: gsap.utils.clamp(w * 0.28, geom.stageW - w * 0.28, x),
    y: gsap.utils.clamp(
      geom.headerPad + h * 0.35,
      mobileMaxCenterY(geom.stageH, h),
      y,
    ),
  };
};

const mobileCardPose = (
  item: (typeof COLLAGE_ITEMS)[number],
  geom: OvalGeom,
  moveT: number,
): { x: number; y: number } => {
  const endAngle = boxAngle(STATE_MOBILE[item.key]);
  const startAngle = endAngle - item.dir * item.travel;
  const startX = geom.cx + geom.rx * Math.cos(startAngle);
  const startY = geom.cy + geom.ry * Math.sin(startAngle);
  const end = mobileFinalCenter(item.key, geom);
  return {
    x: startX + (end.x - startX) * moveT,
    y: startY + (end.y - startY) * moveT,
  };
};

export const BiomarkersScienceSection = ({
  'aria-label': ariaLabel,
}: {
  'aria-label'?: string;
}) => {
  const t = useTranslations('homepage');

  const outerRef = useRef<HTMLElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const textStackRef = useRef<HTMLDivElement | null>(null);
  const collageRefs = useRef<Record<CollageKey, HTMLDivElement | null>>({
    blockBackLeft: null,
    blockFrontLeft: null,
    flowerImageCard: null,
    blockBackRight: null,
    blockFrontRight: null,
    wavesImageCard: null,
  });

  const gsapCtx = useGsapContext(outerRef);

  useIsomorphicLayoutEffect(() => {
    gsapCtx.add(() => {
      const mm = gsap.matchMedia();

      mm.add(PINNED_MEDIA_QUERY, () => {
        const outer = outerRef.current;
        const stage = stageRef.current;
        const textStack = textStackRef.current;
        if (!outer || !stage || !textStack) return;

        COLLAGE_ITEMS.forEach(({ key }) => {
          const el = collageRefs.current[key];
          if (el) gsap.set(el, { clearProps: 'transform' });
        });

        const unbindReveal = bindOnceTextReveal(textStack);

        const proxies = COLLAGE_ITEMS.map((item) => ({
          ...item,
          el: collageRefs.current[item.key],
          box: STATE_B[item.key],
          z: COLLAGE_Z[item.key],
        }));

        const localPAt = (appearAt: number, duration: number, v: number) => {
          const start = appearAt / COLLAGE_ANIM_END;
          const span = duration / COLLAGE_ANIM_END;
          return gsap.utils.clamp(0, 1, (v - start) / Math.max(span, 0.001));
        };

        let geom = measureGeom(stage);

        const applyCardSizes = () => {
          proxies.forEach((item) => {
            if (!item.el) return;
            const { w, h } = cardSize(item.key, geom);
            item.el.style.setProperty('--card-w', `${w}px`);
            item.el.style.setProperty('--card-h', `${h}px`);
          });
        };

        const paintCollage = (v: number) => {
          proxies.forEach((item) => {
            if (!item.el) return;

            const orbit = ORBIT_EASE(localPAt(item.appearAt, item.duration, v));

            if (geom.isDesktop) {
              const angles = desktopAngles(item.box);
              const angle =
                angles.start + (angles.end - angles.start) * orbit;
              gsap.set(item.el, {
                x: geom.cx + geom.rx * Math.cos(angle),
                y: geom.cy + geom.ry * Math.sin(angle),
                xPercent: -50,
                yPercent: -50,
                scale: orbit,
                zIndex: item.z,
                force3D: true,
              });
              return;
            }

            const pose = mobileCardPose(item, geom, orbit);
            const { h } = cardSize(item.key, geom);
            const y = Math.min(
              pose.y,
              mobileMaxCenterY(geom.stageH, h, orbit),
            );
            gsap.set(item.el, {
              x: pose.x,
              y,
              xPercent: -50,
              yPercent: -50,
              transformOrigin: '50% 50%',
              scale: orbit,
              zIndex: item.z,
              force3D: true,
            });
          });
        };

        applyCardSizes();
        paintCollage(0);

        const travel = Math.max(1, outer.offsetHeight);
        const coarsePointer = window.matchMedia(COARSE_POINTER_QUERY).matches;
        const ovalProgress = { value: 0 };
        const ovalTl = gsap.timeline({
          scrollTrigger: {
            trigger: outer,
            start: () =>
              window.matchMedia(DESKTOP_LAYOUT_QUERY).matches
                ? DESKTOP_SCROLL_START
                : MOBILE_SCROLL_START,
            end: () => `+=${travel}`,
            scrub: coarsePointer ? MOBILE_SCRUB_LAG : true,
            invalidateOnRefresh: true,
            onRefresh: () => {
              geom = measureGeom(stage);
              applyCardSizes();
              paintCollage(ovalProgress.value);
            },
          },
        });

        ovalTl.to(
          ovalProgress,
          {
            value: 1,
            duration: 1,
            ease: 'none',
            onUpdate: () => paintCollage(ovalProgress.value),
          },
          0,
        );

        return () => {
          unbindReveal();
          ovalTl.scrollTrigger?.kill();
          ovalTl.kill();
        };
      });

      return () => mm.revert();
    });
  }, [gsapCtx]);

  const titleId = 'biomarkers-science-title';

  return (
    <section ref={outerRef} className={styles.outer} aria-label={ariaLabel}>
      <div ref={stageRef} className={styles.stage}>
        {COLLAGE_ITEMS.map(({ key }) => (
          <div
            key={key}
            aria-hidden
            className={styles.card}
            ref={(node: HTMLDivElement | null) => {
              collageRefs.current[key] = node;
            }}
          >
            <CustomImage
              src={SCIENCE_IMAGES[key]}
              alt=""
              fill
              sizes="(max-width: 991px) 22vw, 25vw"
              style={{ objectFit: 'cover' }}
            />
          </div>
        ))}

        <div
          ref={textStackRef}
          className={styles.textStack}
          aria-labelledby={titleId}
        >
          <h2 id={titleId} className={`${v3.sectionTitle} ${styles.title}`}>
            {t('homepage.biomarkersScience.title')}
          </h2>
          <p className={`${v3.body} ${styles.body}`}>
            {t('homepage.biomarkersScience.body')}
          </p>
          <CustomLink
            href="/science/studies"
            className={`${v3.pill} ${v3.pillSecondary} ${styles.cta}`}
            {...homepageCtaProps('science_section_read_button', [
              'science_library_read_button',
              'ring_pro_banner_science_card',
            ])}
            aria-label={t('homepage.biomarkersScience.aria.readScience')}
          >
            {t('homepage.biomarkersScience.readScience')}
          </CustomLink>
        </div>
      </div>
    </section>
  );
};

export default BiomarkersScienceSection;
